import { Stack } from "expo-router";
import { useState } from "react";
import { Text, View } from "react-native";
import { AgeRangeMap, GenderMap } from "~/@types/qendo/types";
import { Button } from "~/components/Button";
import { ErrorModal } from "~/components/feedback/ErrorModal";
import { DeleteAccount } from "~/components/form/team/DeleteAccount";
import { LeaveTeam } from "~/components/form/team/LeaveTeam";
import { HeaderButton } from "~/components/HeaderButton";
import { Avatar } from "~/components/layout/Avatar";
import { DataList } from "~/components/layout/DataList";
import { DataListItem } from "~/components/layout/DataListItem";
import { FullPageModal } from "~/components/layout/modal/FullPageModal";
import { Page } from "~/components/layout/Page";
import { Spacer } from "~/components/layout/Spacer";
import { BaseText, Title } from "~/components/layout/text";
import { Can } from "~/components/permission/Can";
import { Only } from "~/components/permission/Only";
import { Gap } from "~/constants/layout";
import { Font } from "~/constants/typography";
import type { ApiError } from "~/providers/ApiProvider";
import { useProfile } from "~/providers/ProfileProvider";
import { useSession } from "~/providers/SessionProvider";
import { useSystemOptions } from "~/providers/SystemOptionsProvider";
import { useUser } from "~/providers/UserProvider";
import { filled } from "~/util";

export default function AccountDetails() {
    const { profile, can } = useProfile();
    const { user, isPrimaryTeam, teams } = useUser();
    const { requestPasswordReset, is_share_user } = useSession();
    const { countries } = useSystemOptions();
    const [requesting, setRequesting] = useState(false);
    const [requested, setRequested] = useState(false);
    const [requestError, setRequestError] = useState<string | null>(null);
    const [deleting, setDeleting] = useState<boolean>(false);
    const [leaving, setLeaving] = useState<boolean>(false);

    const country = countries.find((c) => c.code === profile?.country);
    const email = user.email;
    const primary = isPrimaryTeam(profile.id);
    const team_member = !primary && !is_share_user;
    const team = teams.find(({ profile_id }) => profile_id === profile.id);
    const can_leave = filled(team) && team_member;

    const changePassword = () => {
        if (requesting || is_share_user) {
            return;
        }
        setRequesting(true);

        requestPasswordReset(email)
            .then(() => setRequested(true))
            .catch((error: ApiError) => {
                setRequestError(
                    error.response?.data?.message ?? "We could not reset your password. Please try again later",
                );
            })
            .finally(() => setRequesting(false));
    };

    const retryPassword = () => {
        if (requesting || !requestError || is_share_user) {
            return;
        }

        setRequestError(null);
        changePassword();
    };

    return (
        <>
            <Stack.Screen
                options={{
                    title: "Account details",
                    headerRight: () => (
                        <Only when={can("update", "profile") && !is_share_user}>
                            <HeaderButton href="/more/account/edit-profile" title="Edit" />
                        </Only>
                    ),
                }}
            />
            <Page spacer>
                <Avatar size={96} />
                <Title size="xl">{profile.name || user.name}</Title>
                <Can ability="view" section="profile">
                    <DataList>
                        <DataListItem title="Contact number" data={profile?.phone} />
                        <Can ability="view" section="medical">
                            <DataListItem
                                title="Age range"
                                data={profile?.age_range && AgeRangeMap[profile.age_range]}
                            />
                            <DataListItem title="Gender" data={profile?.gender && GenderMap[profile.gender]} />
                        </Can>
                        <DataListItem title="Postcode" data={profile?.postcode} />
                        <DataListItem title="Country" data={country?.name} />
                    </DataList>
                </Can>
                <Only when={primary}>
                    <Button
                        title="Change password"
                        variant="secondary"
                        size="lg"
                        onPress={changePassword}
                        loading={requesting}
                    />
                    <Spacer />
                    <View style={[Gap.sm]}>
                        <Button
                            icon="trash"
                            title="Remove profile data"
                            variant="destructive"
                            size="lg"
                            onPress={() => setDeleting(true)}
                        />
                        <BaseText size="sm" align="center">
                            All of your information will be removed from the app.
                        </BaseText>
                    </View>
                </Only>
                <Only when={can_leave}>
                    <Spacer />
                    <View style={[Gap.sm]}>
                        <Button icon="trash" title="Leave team" variant="destructive" size="lg" />
                    </View>
                </Only>
            </Page>
            {requestError && (
                <ErrorModal description={requestError} retry={retryPassword} onDismiss={() => setRequestError(null)} />
            )}
            {deleting && <DeleteAccount onRequestClose={() => setDeleting(false)} />}
            {can_leave && leaving && <LeaveTeam team={team} onRequestClose={() => setLeaving(false)} />}
            {requested && (
                <FullPageModal title="Change password" onRequestClose={() => setRequested(false)} withDone>
                    <BaseText>
                        We&apos;ve sent an email to <Text style={Font.semibold}>{email}</Text> with a link and
                        instructions to change your password.
                    </BaseText>
                </FullPageModal>
            )}
        </>
    );
}
